import { Fragment } from 'react';
import { useHistory } from 'react-router-dom';
import { ChevronRight } from '@parrot-software/design-system/icons';
import { cn } from '@parrot-software/utils';

import { Breadcrumb } from '@personnel/presentation/types';

interface BreadcrumbsProps {
  items: Breadcrumb[];
}

/**
 * Breadcrumbs Component
 *
 * Navigation breadcrumb trail for hierarchical navigation.
 * Uses Semantic Styles Pattern for maintainability.
 *
 * @param {BreadcrumbsProps} props - Component properties
 * @param {Breadcrumb[]} props.items - Array of breadcrumb items with label and optional path
 * @returns {React.ReactElement} Breadcrumbs component
 *
 * @example
 * <Breadcrumbs
 *   items={[
 *     { label: 'Roles', path: '/roles' },
 *     { label: 'Edit role' }
 *   ]}
 * />
 */
const styles = {
  nav: 'flex items-center gap-2',
  separator: 'size-4 text-muted-foreground',
  breadcrumb: {
    base: 'text-sm font-medium',
    clickable: 'text-muted-foreground hover:text-foreground cursor-pointer',
    current: 'text-foreground cursor-default',
  },
};

export const Breadcrumbs = ({ items }: BreadcrumbsProps): React.ReactElement => {
  const history = useHistory();

  const handleBreadcrumbClick = (path?: string) => {
    if (path) {
      history.push(path);
    }
  };

  return (
    <nav className={styles.nav} style={{ gridArea: 'breadcrumbs' }}>
      {items.map((breadcrumb, index) => {
        const isLast = index === items.length - 1;
        const isClickable = !!breadcrumb.path && !isLast;

        return (
          <Fragment key={`${breadcrumb.label}-${index}`}>
            {index > 0 && <ChevronRight className={styles.separator} />}
            <button
              className={cn(
                styles.breadcrumb.base,
                isClickable && styles.breadcrumb.clickable,
                isLast && styles.breadcrumb.current,
              )}
              disabled={!isClickable}
              onClick={() => handleBreadcrumbClick(breadcrumb.path)}
              type="button"
            >
              {breadcrumb.label}
            </button>
          </Fragment>
        );
      })}
    </nav>
  );
};
